import type { LifecycleStep } from './lifecycle'

/**
 * Roadmap #15 "case progress metrics" — a compact, at-a-glance summary of a Case's position in its
 * lifecycle: percent complete, current stage label, and a "done / total" stage count. Pure and
 * jurisdiction-agnostic (no `if (state === ...)`/`if (program === ...)` branch anywhere in this
 * file, matching `lifecycle.ts`'s own posture) so it is unit-testable in isolation and identical for
 * any state/program.
 */

export interface CaseProgress {
  /** Stages with status `'done'`. */
  doneCount: number
  /** Total stages in the backbone this progress was derived from. */
  totalCount: number
  /** `round(doneCount / totalCount * 100)`, `0` when `totalCount` is `0` (never `NaN`/`Infinity`). */
  percentComplete: number
  /** The current stage's label, `'Complete'` once every stage is done, or the first stage's label
   * for a `totalCount === 0` backbone (never reached by either derivation below, but kept total). */
  currentStageLabel: string
}

/**
 * The full-fidelity derivation — reuses `deriveLifecycle`'s six-stage output (`lifecycle.ts`) as its
 * backbone (the story's own instruction: "reuse deriveLifecycle as the backbone of the metric").
 * Used on the Case Overview tab, where every stage's real inputs (filings, qualified spend, certified
 * instruments, transfers, dossiers) are already resolved by the caller — no fetch happens here.
 */
export function deriveCaseProgress(steps: readonly LifecycleStep[]): CaseProgress {
  const totalCount = steps.length
  const doneCount = steps.filter((s) => s.status === 'done').length
  const percentComplete = totalCount === 0 ? 0 : Math.round((doneCount / totalCount) * 100)
  const current = steps.find((s) => s.status === 'current')
  const currentStageLabel = current
    ? current.label
    : totalCount > 0 && doneCount === totalCount
      ? 'Complete'
      : (steps[0]?.label ?? 'Not started')
  return { doneCount, totalCount, percentComplete, currentStageLabel }
}

/**
 * The coarse fallback — used ONLY where the full lifecycle backbone's inputs are not already loaded
 * (the Cases list: `CaseListView`, unlike the Case Overview, carries no per-case filings/credit-
 * instrument/transfer/dossier data, and fetching that per row — potentially across several modules,
 * for every visible row on every page — is explicitly out of scope for this story; see the story's
 * own "skip rather than add a fetch" guidance). The only stage-relevant signal a `CaseListView` row
 * actually carries is `Case.overallStatus` ('open'/'closed'), so this derives a real two-stage
 * reading from that field alone — never a fabricated multi-stage percentage. Jurisdiction- AND
 * kind-agnostic: the exact same two stages for an R&D or a Film & Media case.
 */
export function deriveCaseProgressFromStatus(overallStatus: string): CaseProgress {
  const closed = overallStatus === 'closed'
  return {
    doneCount: closed ? 2 : 1,
    totalCount: 2,
    percentComplete: closed ? 100 : 50,
    currentStageLabel: closed ? 'Closed' : 'Open',
  }
}
